/**
 * The post-answer reveal panel — correct/wrong banner + rationale +
 * Next button. Identical across every quiz; consolidated here.
 *
 * Routes provide `answerLine` (the prominent answer-line text) and
 * `rationale` (1–3 sentence explanation). The colored banner styling
 * + Next button live here.
 */

import type { ReactNode } from "react";

interface RevealPanelProps {
  isCorrect: boolean;
  /** Inline answer line, e.g. <>Answer: <strong>25%</strong></>. */
  answerLine: ReactNode;
  /** 1–3 sentence rationale. */
  rationale: ReactNode;
  onNext: () => void;
}

export function RevealPanel({ isCorrect, answerLine, rationale, onNext }: RevealPanelProps) {
  return (
    <div className="space-y-4">
      <div
        className={[
          "rounded-xl p-4 border space-y-2",
          isCorrect
            ? "border-success/40 bg-success/10"
            : "border-danger/40 bg-danger/10",
        ].join(" ")}
      >
        <div className="flex items-baseline gap-2">
          <span className={`text-sm font-semibold ${isCorrect ? "text-success" : "text-danger"}`}>
            {isCorrect ? "Correct." : "Wrong."}
          </span>
          <span className="text-sm text-text">{answerLine}</span>
        </div>
        <p className="text-xs text-text-muted leading-relaxed">{rationale}</p>
      </div>

      <button
        type="button"
        onClick={onNext}
        autoFocus
        className="w-full py-3 rounded-xl bg-accent text-white font-medium hover:bg-accent/90 transition active:scale-[0.98]"
      >
        Next question
      </button>
    </div>
  );
}
